import type { ClipboardEvent } from 'react'
import type { components } from '@/api/schema'
import type { QuoteBuilderApi } from '../api/client'

// The quote Files library (USA-1023), ported from the demo's features/files/api.ts.

export type QuoteFile = components['schemas']['QbQuoteFileRead']
export type FileUpload = components['schemas']['QbQuoteFileUpload']

export const MAX_FILE_BYTES = 50 * 1024 * 1024
export const MAX_IMPORT_BYTES = 25 * 1024 * 1024

export const fileTypes: Record<string, string> = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
  txt: 'text/plain',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  // An AutoQuotes project file, imported through the equipment review from the chat (USA-1342).
  aqproj: 'application/x-aqproj',
}
export const fileAccept = Object.keys(fileTypes).map((extension) => `.${extension}`).join(',')

const extension = (name: string) => name.split('.').pop()?.toLowerCase() || ''

/** A quote's files, or (before a quote exists) the signed-in user's own unassigned files. */
export const quoteFilesKey = (quoteId?: string | null) =>
  quoteId ? (['quote-files', quoteId] as const) : (['quote-files', 'unassigned'] as const)

export function validateFile(file: File) {
  if (!fileTypes[extension(file.name)]) return 'Choose a PDF, Excel, CSV, text or AutoQuotes file, or a PNG, JPEG, WebP or GIF image.'
  if (!file.size) return 'This file is empty. Choose a file with content.'
  if (file.size > MAX_FILE_BYTES) return 'This file is larger than 50 MB. Choose a smaller file.'
  return null
}

export function formatFileSize(bytes: number) {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export const isSpreadsheet = (file: QuoteFile) =>
  file.content_type === fileTypes.xlsx || file.content_type === fileTypes.xls

function friendly(error: unknown): never {
  const status = (error as { status?: number }).status
  if (status === 404) throw new Error('This file is no longer available. Refresh the file list.')
  if (status === 413) throw new Error('This file is larger than 50 MB. Choose a smaller file.')
  if (status === 415 || status === 422) throw new Error('This file could not be read. Check its format and try again.')
  if (status === 503) throw new Error('File storage is unavailable right now. Try again shortly.')
  throw new Error('Could not finish the file request. Try again.')
}

async function sha256(file: File) {
  if (!crypto.subtle) throw new Error('Open the dashboard over HTTPS to upload files securely.')
  const hash = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function quoteFilesApi(api: QuoteBuilderApi) {
  const read = <T,>(path: string, init?: RequestInit) => api.request<T>(path, init).catch(friendly)
  return {
    list: (quoteId?: string | null, signal?: AbortSignal) =>
      read<QuoteFile[]>(`/quote-files${quoteId ? `?quote_id=${encodeURIComponent(quoteId)}` : ''}`, { signal }),
    async initiate(file: File, quoteId: string | null) {
      const invalid = validateFile(file)
      if (invalid) throw new Error(invalid)
      return read<FileUpload>('/quote-files', {
        method: 'POST',
        body: JSON.stringify({
          quote_id: quoteId,
          original_file_name: file.name,
          content_type: fileTypes[extension(file.name)],
          file_size_bytes: file.size,
          sha256: await sha256(file),
        }),
      })
    },
    put: (upload: FileUpload, file: File, progress: (percent: number) => void) =>
      new Promise<void>((resolve, reject) => {
        // Send only the signed storage headers, never the dashboard's bearer token.
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', upload.upload_url)
        for (const [key, value] of Object.entries(upload.upload_headers)) xhr.setRequestHeader(key, value)
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) progress(Math.round((event.loaded / event.total) * 100))
        }
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed. Retry this file.')))
        xhr.onerror = () => reject(new Error('The upload connection stopped. Retry this file.'))
        xhr.ontimeout = () => reject(new Error('The upload timed out. Retry this file.'))
        xhr.timeout = 10 * 60 * 1000
        xhr.send(file)
      }),
    complete: (id: string) => read<QuoteFile>(`/quote-files/${encodeURIComponent(id)}/complete`, { method: 'POST' }),
    remove: (id: string) => read<void>(`/quote-files/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    url: (id: string, signal?: AbortSignal) => read<{ url: string }>(`/quote-files/${encodeURIComponent(id)}/url`, { signal }),
    preview: (id: string, signal?: AbortSignal) =>
      read<{ text: string }>(`/quote-files/${encodeURIComponent(id)}/preview`, { signal }),
  }
}

export type QuoteFilesApi = ReturnType<typeof quoteFilesApi>

/** Files pasted into a text field (the assistant composer), demo `pastedFiles`. */
export function pastedFiles(event: ClipboardEvent): File[] {
  const direct = Array.from(event.clipboardData.files)
  const files = direct.length
    ? direct
    : Array.from(event.clipboardData.items || []).flatMap((item) => {
        const file = item.kind === 'file' ? item.getAsFile() : null
        return file ? [file] : []
      })
  if (files.length) event.preventDefault()
  return files
}
